import { Box, Grid, makeStyles } from "@material-ui/core";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import useFilterSync from "hooks/useFilterSync";
import { useModal } from "hooks/useModal";
import { useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import CooperativeMemberDetailsModal from "./CooperativeMemberDetailsModal";
import CooperativeMembersModal from "./CooperativeMembersModal";
import FilterSection from "./FilterSection";
import ResultSection from "./ResultSection";
import {
  buildCooperativeMembersQueryParams,
  COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES,
} from "./cooperativeMember.utils";

const useStylesGeneric = makeStyles(componentStylesGeneric);
const QUERY_PARAMS_PREFIX = "cm_";

function CooperativeMembersContent({ cooperativeId }) {
  const classes = useStylesGeneric();
  const location = useLocation();
  const { getValues, setValue } = useFormContext();
  const { isOpen, openModal, closeModal } = useModal();
  const { isOpen: isDetailsOpen, openModal: openDetailsModal, closeModal: closeDetailsModal } = useModal();

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const sortStateRef = useRef({
    sortColumn: null,
    sortDirection: "asc",
  });

  const hasInitialSearchParams = Boolean(location.search);
  const [queryParams, setQueryParams] = useState(() =>
    hasInitialSearchParams
      ? null
      : buildCooperativeMembersQueryParams(COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES, cooperativeId, null, "asc"),
  );

  const buildQueryParams = (values, overrides = {}) =>
    buildCooperativeMembersQueryParams(
      values,
      cooperativeId,
      overrides.sortColumn ?? sortStateRef.current.sortColumn,
      overrides.sortDirection ?? sortStateRef.current.sortDirection,
      {
        pageIndex: overrides.pageIndex ?? values.pageIndex ?? 1,
      },
    );

  const { handleFilterSubmit, handleFilterReset, updatePagination } = useFilterSync({
    defaultValues: COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES,
    syncPagination: true,
    queryParamsPrefix: QUERY_PARAMS_PREFIX,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES.pageSize,
    },
    onFilterChange: (values) => {
      setQueryParams(buildQueryParams(values || getValues()));
    },
  });

  const submitFilters = (overrides = {}) => {
    const currentValues = getValues();
    const nextValues = {
      ...currentValues,
      pageIndex: overrides.pageIndex ?? 1,
      pageSize: currentValues.pageSize ?? COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES.pageSize,
    };

    if ((currentValues.pageIndex ?? 1) !== nextValues.pageIndex) {
      setValue("pageIndex", nextValues.pageIndex);
    }

    handleFilterSubmit(nextValues);
  };

  const handleSearch = () => {
    submitFilters({ pageIndex: 1 });
  };

  const handleClear = () => {
    sortStateRef.current = {
      sortColumn: null,
      sortDirection: "asc",
    };
    setSortColumn(null);
    setSortDirection("asc");
    handleFilterReset();
  };

  const handleAutoSearch = () => {
    submitFilters({ pageIndex: 1 });
  };

  const handleSortChange = (column, direction) => {
    sortStateRef.current = {
      sortColumn: column,
      sortDirection: direction,
    };
    setSortColumn(column);
    setSortDirection(direction);
    submitFilters({ pageIndex: 1 });
  };

  const handlePageChange = (newPage) => {
    const currentValues = getValues();
    const nextValues = {
      ...currentValues,
      pageIndex: newPage,
    };

    setValue("pageIndex", newPage);
    updatePagination(newPage, currentValues.pageSize ?? COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES.pageSize);
    setQueryParams(buildQueryParams(nextValues, { pageIndex: newPage }));
  };

  const handleViewDetails = (memberId) => {
    setSelectedMemberId(memberId);
    openDetailsModal();
  };

  return (
    <>
      <Box>
        <Grid container>
          <Grid item xs={12} md={12} classes={{ root: classes.gridItem }}>
            <PageTitleHeader title="Membros da Cooperativa" />
          </Grid>
          <Grid item xs={12} md={12}>
            <FilterSection
              onSearch={handleSearch}
              onClear={handleClear}
              onAddMember={openModal}
              onAutoSearch={handleAutoSearch}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <ResultSection
              queryParams={queryParams}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onPageChange={handlePageChange}
              onViewDetails={handleViewDetails}
            />
          </Grid>
        </Grid>
      </Box>

      {isOpen && (
        <CooperativeMembersModal
          openDialog={isOpen}
          handleCloseDialog={closeModal}
          cooperativeId={cooperativeId}
        />
      )}

      {isDetailsOpen && (
        <CooperativeMemberDetailsModal
          open={isDetailsOpen}
          handleClose={closeDetailsModal}
          memberId={selectedMemberId}
        />
      )}
    </>
  );
}

export default function CooperativeMembers({ cooperativeId }) {
  const form = useForm({
    mode: "onChange",
    defaultValues: COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES,
  });

  return (
    <FormProvider {...form}>
      <CooperativeMembersContent cooperativeId={cooperativeId} />
    </FormProvider>
  );
}
